// src/App.js - COMPLETE WORKING VERSION
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import './App.css';

const ANIMATION_TYPES = {
  FADE_IN: 'fade-in',
  SLIDE_IN: 'slide-in',
};

const AnimatedLogo = ({ className = "", animationType = ANIMATION_TYPES.FADE_IN }) => {
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    const delays = [200, 400, 600];
    const timers = delays.map((delay, i) => 
      setTimeout(() => setAnimationStage(i + 1), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [animationType]);

  return (
    <div className={`animated-logo ${className} ${animationType}`}>
      <span className={`logo-vx ${animationStage >= 1 ? 'animate' : ''}`}>VX</span>
      <span className={`logo-path ${animationStage >= 2 ? 'animate' : ''}`}>PATH</span>
      <span className={`logo-tech ${animationStage >= 3 ? 'animate' : ''}`}>Technology</span>
    </div>
  );
};

const Navbar = ({ logoAnimation }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="nav-logo-link" onClick={() => setIsMobileMenuOpen(false)}>
          <AnimatedLogo animationType={logoAnimation} />
        </Link>

        <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          <li>
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          </li>
          <li className="nav-item-dropdown">
            <Link to="/services" className={`nav-link ${location.pathname === '/services' || location.pathname.startsWith('/services/') ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Services</Link>
            <ul className="dropdown-menu">
              <li><a href="/services#network" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>🌐 Network</a></li>
              <li><a href="/services#net-security" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>🛡️ Security</a></li>
              <li><a href="/services#wireless" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>📡 Wireless</a></li>
              <li><a href="/services#cloud-network" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>☁️ Cloud Network</a></li>
              <li><a href="/services#ai-automation" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>🤖 Ai & Automation</a></li>
              <li><a href="/services#net-technician" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>🔧 Technician</a></li>
              <li><a href="/services#surveillance" className="dropdown-link" onClick={() => setIsMobileMenuOpen(false)}>📹 Surveillance</a></li>
              
            </ul>
          </li>
          <li><Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>About Us</Link></li>
          <li><Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Contact</Link></li>
          <li><Link to="/blog" className={`nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Blog</Link></li>
          <li className="mobile-cta">
            <Link to="/contact" className="btn-primary" onClick={() => setIsMobileMenuOpen(false)}>Get Started</Link>
          </li>
        </ul>

        <button className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>
    </nav>
  );
};

const Footer = ({ logoAnimation }) => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <AnimatedLogo className="footer-logo-animated" animationType={logoAnimation} />
          <div className="footer-links">
            <Link to="/services">Services</Link>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/blog">Blog</Link>
          </div>
          <div className="footer-social">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
            </a>
            <a href="https://x.com" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {currentYear} Vxpath Technology. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  const [logoAnimation] = useState(ANIMATION_TYPES.FADE_IN);

  return (
    <Router>
      <div className="app">
        <Navbar logoAnimation={logoAnimation} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/service/:slug" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="*" element={
            <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
              <h2>Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
              <Link to="/" className="btn-primary">Back to Home</Link>
            </div>
          } />
        </Routes>
        <Footer logoAnimation={logoAnimation} />
      </div>
    </Router>
  );
}

export default App;